import { CanActivateFn, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { ErrorHandler, inject } from '@angular/core';
import { AccountService } from '../services/account.service';
import { LiffService } from 'src/app/liff/liff.service';
import { TransferLiffService } from 'src/app/liff/transfer/transfer-liff.service';
import { FirebaseSessionService } from '../services/firebase-session.service';

function isAppPhoneRegistrationRoute(stateUrl: string): boolean {
  const [path, queryString = ''] = stateUrl.split('?');
  if (!path.startsWith(environment.redirectPaths.user.registration)) {
    return false;
  }

  return new URLSearchParams(queryString).get('sourceFlow') === 'app-phone';
}

/**
 * 僅辨識含邀請碼的 HCP 註冊入口；不可併入病患註冊系列，避免略過條款檢查。
 */
function isPhysicianRegistrationQrRoute(stateUrl: string): boolean {
  const [path, queryString = ''] = stateUrl.split('?');
  return path === environment.redirectPaths.physician.registration && !!new URLSearchParams(queryString).get('code');
}

function isUserRegistrationSeriesRoute(stateUrl: string): boolean {
  return (
    stateUrl.startsWith(environment.redirectPaths.user.registration) ||
    stateUrl.startsWith(environment.redirectPaths.user.hospitalQrcode) ||
    stateUrl.startsWith(environment.redirectPaths.user.invitationQrcode) ||
    stateUrl.startsWith(environment.redirectPaths.user.medicineQrcode)
  );
}

/**
 * 進入各個主要頁面前的身份驗證守衛
 * @param route
 * @param state
 */
export const authGuard: CanActivateFn = async (route, state) => {
  const accountService = inject(AccountService);
  const router = inject(Router);
  const liff = inject(LiffService);
  const transferLiff = inject(TransferLiffService);
  const firebaseSession = inject(FirebaseSessionService);
  const errorHandler = inject(ErrorHandler);

  try {
    const decodedLiffState = decodeURIComponent(new URLSearchParams(location.search).get('liff.state') || '');
    // 從舊 OA 開啟既有用戶轉移頁面，使用舊的 LIFF ID 執行 initLiff 並直接進入轉移頁面
    if (
      decodedLiffState.startsWith(environment.redirectPaths.transfer.previousOfficialAccount) ||
      state.url.startsWith(environment.redirectPaths.transfer.previousOfficialAccount)
    ) {
      await transferLiff.initLiff(environment.liff.previousLiffId);
      return true;
    }

    // 因 iOS 從 LINE 外面開啟 LIFF 連結時不會自動回到 LINE 開啟 LIFF Browser，而是開啟外部瀏覽器導致取不到 LINE User ID，所以阻擋在外部瀏覽器瀏覽 LIFF 連結的行為（已確認過不需顯示 LINE 手動登入頁）
    if (!liff.isInClient() && new URLSearchParams(location.search).has('liff.state')) {
      // 使用電腦瀏覽器進入 僅開放教育視訊通話 Liff 且需手動登入，30天 Liff token 會自動過期，須再登入一次
      if (state.url.startsWith(environment.redirectPaths.educationVideoCall)) {
        await liff.initLiff(environment.liff.liffId);
      }

      // 其餘不開放的頁面一律阻擋
      alert('請關閉此頁面，並使用「LINE 內建掃描器」完成驗證');
      return router.parseUrl(environment.redirectPaths.noAccess);
    }
    // 只有在 LIFF Browser 中才當成 LIFF 來執行，其餘都當成一般網頁
    // user在登入之後會重新導向到afterLogin頁面，此時不需要再次initLiff
    else if (
      !state.url.startsWith(environment.redirectPaths.user.afterLogin) &&
      !isAppPhoneRegistrationRoute(state.url) &&
      (liff.isInClient() || new URLSearchParams(location.search).has('liff.state'))
    ) {
      await liff.initLiff(environment.liff.liffId);
    }

    // 確保使用者已經登入
    const account = await accountService.getMyAccount();
    await firebaseSession.ensureSession(account.id);

    // 同意條款頁面不做阻擋
    if (state.url.startsWith(environment.redirectPaths.userAgreement)) {
      return true;
    }

    const isRegistrationSeriesRoute = isUserRegistrationSeriesRoute(state.url);
    const isPhysicianRegistrationQr = isPhysicianRegistrationQrRoute(state.url);
    // 註冊系列頁面本身已內嵌條款勾選，不在這裡先攔去 user-agreement，
    // 否則 App phone step2 與既有 LIFF 註冊頁都會在正式 submit 前被導離。
    // 如果同意條款尚未勾選同意，就回到同意條款頁面
    if (!account.agreeTerms && !isRegistrationSeriesRoute) {
      return router.parseUrl(`${environment.redirectPaths.userAgreement}?redirectUrl=${encodeURIComponent(state.url)}`);
    }

    // 計畫同意書頁面不做阻擋
    if (state.url.startsWith(environment.redirectPaths.programConsentForm)) {
      return true;
    }

    // 使用電腦瀏覽器進入 僅開放教育視訊通話 Liff 頁面不做阻擋
    if (state.url.startsWith(environment.redirectPaths.educationVideoCall)) {
      return true;
    }

    // 如果角色是病患就做登入後的檢查，確保該病患有通過驗證
    // 註冊系列頁面依 PRD / STD 規格直接放行，讓 general / user 都可進入表單。
    if (
      account.role === 'user' &&
      !state.url.startsWith(environment.redirectPaths.user.afterLogin) &&
      !isRegistrationSeriesRoute &&
      !isPhysicianRegistrationQr
    ) {
      return router.parseUrl(
        `${environment.redirectPaths.user.afterLogin}?redirectUrl=${encodeURIComponent(state.url)}`
      );
    }

    return true;
  } catch (err) {
    await firebaseSession.clearSession().catch((signOutError) => {
      console.error('Failed to clear Firebase session', signOutError);
    });
    // 注意：在 Guard 裡面執行 return false 或 throw Error() 都會回到根目錄，此轉址優先於 Error Handler，因此這邊需要先觸發 Error Handler 以防抓不到網址資訊
    errorHandler.handleError(err);
    return false;
  }
};
